// SEO Service for APN Website
// Comprehensive SEO optimization and meta tag management

export const SEO_CONFIG = {
  siteName: 'The APN - Accelerated Professionals Network',
  siteUrl: 'https://the-apn.com',
  defaultTitle: 'The APN | Accelerated Professionals Network | #1 Professional Network',
  defaultDescription: 'The APN (Accelerated Professionals Network) - The premier accelerated professional network connecting finance professionals worldwide. Join the leading professional network for accelerated career growth.',
  defaultKeywords: [
    // Primary target keywords for top rankings
    'the apn',
    'accelerated professionals network',
    'accelerated professionals',
    'professional network',
    'accelerated',
    'professional',
    'network',
    // Brand variations
    'the accelerated professionals network',
    'apn network',
    'apn professionals',
    // Industry-specific long-tail keywords
    'finance professionals network',
    'investment banking careers',
    'M&A professionals',
    'private equity network',
    'corporate finance jobs',
    'financial services networking',
    'finance career development',
    'investment banking community',
    'financial professionals association',
    'finance networking events'
  ],
  author: 'Accelerated Professionals Network',
  twitterHandle: '@the_apn_',
  linkedinUrl: 'https://www.linkedin.com/company/accelerated-professionals-network',
  instagramUrl: 'https://www.instagram.com/the_apn_/',
  logo: '/apn-logos/APN-Tag-SQ-blue.png',
  favicon: '/favicon.ico'
};

// Page-specific SEO configurations optimized for top rankings
export const PAGE_SEO_CONFIG = {
  home: {
    title: 'The APN | #1 Accelerated Professionals Network | Professional Network',
    description: 'The APN - The premier accelerated professionals network. Join the leading professional network connecting accelerated professionals worldwide. The top choice for professional networking and accelerated career growth.',
    keywords: [
      // Primary target keywords
      'the apn', 'accelerated professionals network', 'accelerated professionals', 'professional network', 'accelerated', 'professional', 'network',
      // Brand variations
      'the accelerated professionals network', 'apn network', 'apn professionals', 'apn community',
      // Intent-based keywords
      'best professional network', 'top professional network', 'premier professional network', 'exclusive professional network',
      'accelerated career growth', 'professional networking platform', 'finance professionals network',
      // Industry-specific
      'investment banking community', 'M&A professionals', 'private equity network', 'finance careers'
    ],
    type: 'website'
  },
  about: {
    title: 'About APN - Leading Finance Professionals Network | Our Story & Mission',
    description: 'Learn about the Accelerated Professionals Network, the premier community connecting finance professionals across investment banking, M&A, private equity, and corporate finance sectors.',
    keywords: ['about APN', 'finance professionals community', 'investment banking network', 'professional development'],
    type: 'website'
  },
  membership: {
    title: 'Join APN - Finance Professionals Membership | Exclusive Benefits & Networking',
    description: 'Become an APN member and unlock exclusive access to finance job opportunities, networking events, professional development, and a community of 1000+ finance professionals.',
    keywords: ['finance membership', 'professional networking', 'investment banking membership', 'finance career benefits'],
    type: 'website'
  },
  events: {
    title: 'Finance Networking Events | APN Professional Development & Industry Conferences',
    description: 'Attend exclusive finance networking events, workshops, and conferences. Connect with investment banking, M&A, and private equity professionals at APN events.',
    keywords: ['finance events', 'networking events', 'investment banking conferences', 'professional development workshops'],
    type: 'website'
  },
  jobs: {
    title: 'Finance Jobs Board | Investment Banking, M&A & Private Equity Careers | APN',
    description: 'Discover exclusive finance job opportunities in investment banking, M&A, private equity, and corporate finance. Access hidden job market through APN professional network.',
    keywords: ['finance jobs', 'investment banking jobs', 'M&A careers', 'private equity positions', 'corporate finance opportunities'],
    type: 'website'
  },
  projects: {
    title: 'Finance Projects Board | Consulting & Freelance Opportunities | APN',
    description: 'Find high-value finance consulting projects and freelance opportunities. Connect with clients seeking M&A, valuation, financial modeling, and due diligence expertise.',
    keywords: ['finance consulting', 'financial modeling projects', 'M&A consulting', 'valuation projects', 'due diligence'],
    type: 'website'
  },
  directory: {
    title: 'Finance Professionals Directory | Connect with Investment Banking & M&A Experts',
    description: 'Browse our directory of verified finance professionals including investment bankers, M&A advisors, private equity professionals, and corporate finance experts.',
    keywords: ['finance professionals directory', 'investment banking experts', 'M&A advisors', 'finance networking'],
    type: 'website'
  },
  groups: {
    title: 'Finance Professional Groups | Industry-Specific Communities | APN',
    description: 'Join specialized finance professional groups for investment banking, private equity, M&A, corporate finance, and more. Engage in industry discussions and knowledge sharing.',
    keywords: ['finance groups', 'professional communities', 'investment banking groups', 'industry networking'],
    type: 'website'
  }
};

// Generate structured data for different content types
export const generateStructuredData = (type, data = {}) => {
  const baseData = {
    '@context': 'https://schema.org',
    '@type': type
  };

  switch (type) {
    case 'Organization':
      return {
        ...baseData,
        name: SEO_CONFIG.siteName,
        url: SEO_CONFIG.siteUrl,
        logo: `${SEO_CONFIG.siteUrl}${SEO_CONFIG.logo}`,
        description: SEO_CONFIG.defaultDescription,
        sameAs: [
          SEO_CONFIG.linkedinUrl,
          SEO_CONFIG.instagramUrl
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          availableLanguage: 'English'
        },
        foundingDate: '2020',
        industry: 'Financial Services',
        numberOfEmployees: '10-50',
        ...data
      };

    case 'WebSite':
      return {
        ...baseData,
        name: SEO_CONFIG.siteName,
        url: SEO_CONFIG.siteUrl,
        description: SEO_CONFIG.defaultDescription,
        publisher: {
          '@type': 'Organization',
          name: SEO_CONFIG.siteName
        },
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SEO_CONFIG.siteUrl}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        },
        ...data
      };

    case 'JobPosting':
      return {
        ...baseData,
        title: data.title,
        description: data.description,
        hiringOrganization: {
          '@type': 'Organization',
          name: data.company,
          sameAs: data.companyUrl
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: data.location
          }
        },
        employmentType: data.jobType,
        datePosted: data.datePosted,
        validThrough: data.validThrough,
        baseSalary: data.salary ? {
          '@type': 'MonetaryAmount',
          currency: 'USD',
          value: {
            '@type': 'QuantitativeValue',
            value: data.salary,
            unitText: 'YEAR'
          }
        } : undefined,
        ...data
      };

    case 'Event':
      return {
        ...baseData,
        name: data.title,
        description: data.description,
        startDate: data.startDate,
        endDate: data.endDate,
        location: {
          '@type': data.isVirtual ? 'VirtualLocation' : 'Place',
          name: data.location,
          address: data.isVirtual ? undefined : {
            '@type': 'PostalAddress',
            addressLocality: data.city,
            addressRegion: data.state
          },
          url: data.isVirtual ? data.virtualUrl : undefined
        },
        organizer: {
          '@type': 'Organization',
          name: SEO_CONFIG.siteName,
          url: SEO_CONFIG.siteUrl
        },
        offers: data.ticketPrice ? {
          '@type': 'Offer',
          price: data.ticketPrice,
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock'
        } : undefined,
        ...data
      };

    case 'Article':
      return {
        ...baseData,
        headline: data.title,
        description: data.excerpt,
        author: {
          '@type': 'Person',
          name: data.author
        },
        publisher: {
          '@type': 'Organization',
          name: SEO_CONFIG.siteName,
          logo: {
            '@type': 'ImageObject',
            url: `${SEO_CONFIG.siteUrl}${SEO_CONFIG.logo}`
          }
        },
        datePublished: data.publishedAt,
        dateModified: data.updatedAt || data.publishedAt,
        image: data.image ? `${SEO_CONFIG.siteUrl}${data.image}` : undefined,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SEO_CONFIG.siteUrl}/news/${data.slug}`
        },
        ...data
      };

    default:
      return baseData;
  }
};

// Update document head with SEO meta tags
export const updateSEOTags = (pageConfig = {}) => {
  const config = { ...PAGE_SEO_CONFIG.home, ...pageConfig };
  
  // Update title
  document.title = config.title;
  
  // Update or create meta tags
  const updateMetaTag = (name, content, property = false) => {
    if (!content) return;
    
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let meta = document.querySelector(selector);
    
    if (!meta) {
      meta = document.createElement('meta');
      if (property) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }
      document.head.appendChild(meta);
    }
    
    meta.setAttribute('content', content);
  };

  // Basic meta tags
  updateMetaTag('description', config.description);
  updateMetaTag('keywords', config.keywords?.join(', '));
  updateMetaTag('author', SEO_CONFIG.author);
  updateMetaTag('robots', 'index, follow');
  
  // Open Graph tags
  updateMetaTag('og:title', config.title, true);
  updateMetaTag('og:description', config.description, true);
  updateMetaTag('og:type', config.type || 'website', true);
  updateMetaTag('og:url', `${SEO_CONFIG.siteUrl}${config.path || ''}`, true);
  updateMetaTag('og:site_name', SEO_CONFIG.siteName, true);
  updateMetaTag('og:image', `${SEO_CONFIG.siteUrl}${config.image || SEO_CONFIG.logo}`, true);
  
  // Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:site', SEO_CONFIG.twitterHandle);
  updateMetaTag('twitter:title', config.title);
  updateMetaTag('twitter:description', config.description);
  updateMetaTag('twitter:image', `${SEO_CONFIG.siteUrl}${config.image || SEO_CONFIG.logo}`);
  
  // LinkedIn specific
  updateMetaTag('linkedin:owner', SEO_CONFIG.linkedinUrl);
};

// Add structured data to page (preserves the static Organization schema in index.html)
export const addStructuredData = (structuredData) => {
  // Remove only dynamically-added structured data (marked with data-dynamic attribute)
  const existingDynamic = document.querySelector('script[type="application/ld+json"][data-dynamic="true"]');
  if (existingDynamic) {
    existingDynamic.remove();
  }

  // Add new structured data with dynamic marker so we don't clobber the static one
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-dynamic', 'true');
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
};

// Generate canonical URL
export const setCanonicalUrl = (path = '') => {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = `${SEO_CONFIG.siteUrl}${path}`;
};

export default {
  SEO_CONFIG,
  PAGE_SEO_CONFIG,
  generateStructuredData,
  updateSEOTags,
  addStructuredData,
  setCanonicalUrl
};
